// Auth0UserContext.tsx - Bridges Auth0 authentication with backend user system
import React, { createContext, useState, useContext, useEffect, ReactNode, useMemo, useCallback } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import LoginErrorModal from '../auth/LoginErrorModal';
import EmailCollectionModal from '../auth/EmailCollectionModal';
import SocialProfileModal from '../auth/SocialProfileModal';
import CompleteProfileModal from '../auth/CompleteProfileModal';
import {
  getCurrentUser,
  getUserByAuth0Id,
  HEADER_AUTH_USER_KEY,
  hasCredentialReadyAuth,
  setAuthToken,
} from '../../services/api';
import { useProgressiveProfile } from './useProgressiveProfile';

interface User {
  id: number;
  username: string;
  email: string;
  role: 'collaborator' | 'admin';
  display_name?: string;
  profile_image?: string;
  creator_type?: string;
  bio?: string;
  auth0_id?: string;
  social_profiles?: Array<{
    platform: string;
    handle: string;
    profile_url: string;
    follower_count?: string;
  }>;
}

interface Auth0UserContextType {
  user: User | null;
  loading: boolean;
  login: (options?: { connection?: string }) => void;
  logout: () => void;
  updateUser: (userData: User) => void;
  register: (userData: any) => Promise<{ success: boolean; error?: string }>;
  loginWithSocial: (provider: string, isSignIn?: boolean) => void;
  loginWithEmailOTP: (email: string, otp?: string) => Promise<{ success: boolean; error?: string; newUser?: boolean }>;
  isAuthenticated: boolean;
  accessToken: string | null;
  setIsRequestingToSpot: (isRequesting: boolean) => void;
  isLoggingOut: boolean;
}

const Auth0UserContext = createContext<Auth0UserContextType | undefined>(undefined);

export const Auth0UserProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const {
    user: auth0User,
    isAuthenticated: auth0IsAuthenticated,
    isLoading: auth0IsLoading,
    loginWithRedirect,
    logout: auth0Logout,
    getAccessTokenSilently,
  } = useAuth0();

  // Initialize user from localStorage (for email OTP users)
  const [user, setUser] = useState<User | null>(() => {
    try {
      const storedUser = getCurrentUser();
      // A cached user record is identity display data, not authentication.
      // Only hydrate it when the browser also has a usable bearer credential
      // or an explicit QA/bot header-auth session.
      if (storedUser && hasCredentialReadyAuth()) return storedUser;
    } catch (e) {
      console.error('❌ Failed to parse stored user:', e);
    }
    return null;
  });

  const [loading, setLoading] = useState(true);
  const [errorModalOpen, setErrorModalOpen] = useState(false);
  const [errorType, setErrorType] = useState<'not_found' | 'no_email' | 'service_error'>('not_found');
  const [accessToken, setAccessToken] = useState<string | null>(null);
  const [isLoggingOut, setIsLoggingOut] = useState(false);

  // Initialize isRequestingToSpot from sessionStorage
  const [isRequestingToSpot, setIsRequestingToSpotState] = useState(() => {
    return sessionStorage.getItem('isRequestingToSpot') === 'true';
  });

  const [isSigningIn, setIsSigningInState] = useState(() => {
    return sessionStorage.getItem('isSigningIn') === 'true';
  });

  const setIsRequestingToSpot = useCallback((value: boolean) => {
    setIsRequestingToSpotState(value);
    if (value) {
      sessionStorage.setItem('isRequestingToSpot', 'true');
    } else {
      sessionStorage.removeItem('isRequestingToSpot');
    }
  }, []);

  const setIsSigningIn = useCallback((value: boolean) => {
    setIsSigningInState(value);
    if (value) {
      sessionStorage.setItem('isSigningIn', 'true');
    } else {
      sessionStorage.removeItem('isSigningIn');
    }
  }, []);

  const clearAuthCredential = useCallback(() => {
    setAccessToken(null);
    setAuthToken(null);
  }, []);

  // Progressive profiling (modals, user creation flow)
  const profile = useProgressiveProfile({
    auth0Logout,
    setUser,
    setLoading,
    setErrorModalOpen,
    isRequestingToSpot,
    clearAuthCredential,
  });

  // Sync Auth0 user with backend user system
  useEffect(() => {
    const syncUser = async () => {
      if (isLoggingOut) return;

      // Header-auth sessions must run before auth0IsLoading guard.
      const isBotLogin = sessionStorage.getItem('botLoginActive') === 'true';
      const isTestLogin = sessionStorage.getItem('testLoginActive') === 'true';
      const headerSessionReady =
        (isTestLogin && !!sessionStorage.getItem('qaAuthSecret')) ||
        (isBotLogin && !!sessionStorage.getItem('botAuthSecret'));
      if ((isTestLogin || isBotLogin) && headerSessionReady) {
        // API helpers prioritize the tab-scoped header session over any bearer
        // token. Do not clear shared Auth0/local credentials: another tab may
        // still be the administrator who launched this bot session.
        const storedUser = getCurrentUser();
        if (storedUser) {
          setUser(storedUser);
          setLoading(false);
          return;
        }
      }
      if ((isTestLogin || isBotLogin) && !headerSessionReady) {
        // An orphaned mode flag is not an authenticated session.
        sessionStorage.removeItem('testLoginActive');
        sessionStorage.removeItem('botLoginActive');
        sessionStorage.removeItem('qaAuthSecret');
        sessionStorage.removeItem('botAuthSecret');
      }

      if (auth0IsLoading) return;

      if (!auth0IsAuthenticated || !auth0User) {
        const existingUser = localStorage.getItem('currentUser');
        if (existingUser && user && hasCredentialReadyAuth()) {
          setLoading(false);
          return;
        }
        setUser(null);
        setAccessToken(null);
        setAuthToken(null);
        localStorage.removeItem('currentUser');
        localStorage.removeItem('userId');
        localStorage.removeItem('username');
        setLoading(false);
        return;
      }

      try {
        const token = await getAccessTokenSilently();
        setAccessToken(token);
        setAuthToken(token);

        let backendUser;
        try {
          if (!auth0User.sub) throw new Error('Auth0 user ID (sub) is missing');
          backendUser = await getUserByAuth0Id(auth0User.sub);
        } catch (error: any) {
          // Only a real 404 means this Auth0 identity needs an account. Treating
          // auth failures, conflicts, and outages as signup previously sent
          // users into a form that could never succeed.
          if (error?.name !== 'NotFoundError') {
            console.error('❌ Could not resolve authenticated account:', error);
            setErrorType('service_error');
            setErrorModalOpen(true);
            setUser(null);
            localStorage.removeItem('currentUser');
            localStorage.removeItem('userId');
            localStorage.removeItem('username');
            setLoading(false);
            return;
          }

          // User doesn't exist — check if rejected before
          const rejectedUserId = sessionStorage.getItem('auth0_rejected_user');
          if (rejectedUserId === auth0User.sub) {
            setErrorType('not_found');
            setErrorModalOpen(true);
            setUser(null);
            setAccessToken(null);
            setAuthToken(null);
            localStorage.removeItem('currentUser');
            localStorage.removeItem('userId');
            localStorage.removeItem('username');
            setLoading(false);
            return;
          }

          // Check if user is allowed to sign up
          if (!isRequestingToSpot || isSigningIn) {
            sessionStorage.setItem('auth0_rejected_user', auth0User.sub || '');
            setErrorType('not_found');
            setErrorModalOpen(true);
            setUser(null);
            setAccessToken(null);
            setAuthToken(null);
            localStorage.removeItem('currentUser');
            localStorage.removeItem('userId');
            localStorage.removeItem('username');
            setLoading(false);
            setIsSigningIn(false);
            return;
          }

          // Every new social account gets one consistent, prefilled setup
          // screen. Facebook accounts without email add an OTP step there;
          // accounts with provider-verified email skip that step.
          setUser(null);
          localStorage.removeItem('currentUser');
          localStorage.removeItem('userId');
          localStorage.removeItem('username');
          profile.setPendingAuth0User(auth0User);
          profile.setCollectedEmail(
            auth0User.email_verified === true ? auth0User.email || '' : ''
          );
          profile.setCompleteProfileOpen(true);
          setLoading(false);
          return;
        }

        setUser(backendUser);
        setIsSigningIn(false);
        if (backendUser) {
          localStorage.setItem('currentUser', JSON.stringify(backendUser));
          localStorage.setItem('userId', String(backendUser.id));
          localStorage.setItem('username', backendUser.username);
        }
      } catch (error: any) {
        if (
          sessionStorage.getItem('testLoginActive') === 'true' ||
          sessionStorage.getItem('botLoginActive') === 'true'
        ) {
          setLoading(false);
          return;
        }
        setUser(null);
        setAccessToken(null);
        setAuthToken(null);
        localStorage.removeItem('currentUser');
        localStorage.removeItem('userId');
        localStorage.removeItem('username');
        setErrorType('service_error');
        setErrorModalOpen(true);
      } finally {
        setLoading(false);
      }
    };

    syncUser();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth0User, auth0IsAuthenticated, auth0IsLoading, getAccessTokenSilently, auth0Logout, isRequestingToSpot, isSigningIn, setIsSigningIn, isLoggingOut]);

  // Handle redirect after logout (for error modal logout)
  useEffect(() => {
    if (!auth0IsLoading && !auth0IsAuthenticated) {
      const returnTo = sessionStorage.getItem('returnAfterLogout');
      if (returnTo) {
        sessionStorage.removeItem('returnAfterLogout');
        sessionStorage.removeItem('auth0_rejected_user');
        sessionStorage.removeItem('isSigningIn');
        window.location.replace(returnTo);
      }
    }
  }, [auth0IsLoading, auth0IsAuthenticated]);

  const login = useCallback((options?: { connection?: string }) => {
    loginWithRedirect({
      authorizationParams: {
        connection: options?.connection,
        prompt: 'select_account',
      },
      appState: { returnTo: '/dashboard' }
    });
  }, [loginWithRedirect]);

  const loginWithSocial = useCallback((provider: string, isSignIn: boolean = false) => {
    sessionStorage.removeItem('auth0_rejected_user');
    if (isSignIn) { setIsSigningIn(true); } else { setIsSigningIn(false); }

    const connectionMap: { [key: string]: string } = {
      google: 'google-oauth2',
      facebook: 'facebook',
      instagram: 'instagram',
      twitter: 'twitter',
      linkedin: 'linkedin',
      github: 'github',
    };

    const connection = connectionMap[provider];
    if (connection) {
      const pendingSpot = sessionStorage.getItem('pendingGuestSpotRequest');

      let returnTo: string;
      if (pendingSpot) {
        returnTo = `/creator/${pendingSpot}/request`;
      } else if (isSignIn) {
        returnTo = '/dashboard';
      } else {
        returnTo = window.location.pathname + window.location.search;
      }

      loginWithRedirect({
        authorizationParams: { connection, prompt: 'select_account' },
        appState: { returnTo }
      });
    }
  }, [loginWithRedirect, setIsSigningIn]);

  const loginWithEmailOTP = useCallback(async (email: string, otp?: string): Promise<{ success: boolean; error?: string; newUser?: boolean }> => {
    const API_URL = process.env.REACT_APP_API_URL;

    try {
      if (!otp) {
        const response = await fetch(`${API_URL}/api/auth/passwordless/send-otp`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email })
        });
        const data = await response.json();
        if (!response.ok) return { success: false, error: data.detail || 'Failed to send code' };
        return { success: true };
      } else {
        const response = await fetch(`${API_URL}/api/auth/passwordless/verify-otp`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email, otp })
        });
        const data = await response.json();
        if (!response.ok) return { success: false, error: data.detail || 'Invalid code' };

        if (data.new_user) {
          if (!isRequestingToSpot) {
            return { success: false, error: 'Account not found. To create an account, start by requesting a guest spot.' };
          }

          setAccessToken(data.access_token);
          setAuthToken(data.access_token);
          profile.setCollectedEmail(email);
          if (data.auth0_id) sessionStorage.setItem('temp_auth0_id', data.auth0_id);
          profile.setCompleteProfileOpen(true);
          return { success: true, newUser: true };
        }

        const backendUser = data.user;
        setUser(backendUser);
        setAccessToken(data.access_token);
        setAuthToken(data.access_token);
        localStorage.setItem('currentUser', JSON.stringify(backendUser));
        localStorage.setItem('userId', String(backendUser.id));
        localStorage.setItem('username', backendUser.username);
        return { success: true };
      }
    } catch (error: any) {
      return { success: false, error: error.message || 'Authentication failed' };
    }
  }, [isRequestingToSpot, profile]);

  const logout = useCallback(() => {
    if (
      sessionStorage.getItem('botLoginActive') === 'true' ||
      sessionStorage.getItem('testLoginActive') === 'true'
    ) {
      sessionStorage.removeItem(HEADER_AUTH_USER_KEY);
      sessionStorage.removeItem('botLoginActive');
      sessionStorage.removeItem('botAuthSecret');
      sessionStorage.removeItem('testLoginActive');
      sessionStorage.removeItem('qaAuthSecret');
      setUser(null);
      setAccessToken(null);
      window.location.reload();
      return;
    }
    setIsLoggingOut(true);
    setUser(null);
    setAccessToken(null);
    setAuthToken(null);
    localStorage.removeItem('currentUser');
    localStorage.removeItem('userId');
    localStorage.removeItem('username');

    sessionStorage.removeItem('auth0_rejected_user');
    sessionStorage.removeItem('isSigningIn');
    sessionStorage.removeItem('botLoginActive');
    sessionStorage.removeItem('botAuthSecret');
    sessionStorage.removeItem('testLoginActive');
    sessionStorage.removeItem('qaAuthSecret');
    sessionStorage.removeItem(HEADER_AUTH_USER_KEY);
    sessionStorage.removeItem('auth0_returnTo');
    sessionStorage.removeItem('isRequestingToSpot');
    localStorage.removeItem('anonymousSessionId');

    Object.keys(sessionStorage).forEach(key => {
      if (key.startsWith('requestDraft:') || key.startsWith('videoPitchDraft:')) {
        sessionStorage.removeItem(key);
      }
    });

    (async () => {
      try {
        const { clearAllVideos } = await import('../../utils/indexedDB');
        await clearAllVideos();
      } catch (err) {
        console.error('❌ Failed to clear IndexedDB on logout:', err);
      }
    })();

    auth0Logout({ logoutParams: { returnTo: window.location.origin } });
  }, [auth0Logout]);

  const updateUser = useCallback((userData: User) => {
    setUser(userData);
    localStorage.setItem('currentUser', JSON.stringify(userData));
    localStorage.setItem('userId', String(userData.id));
    localStorage.setItem('username', userData.username);
  }, []);

  const register = useCallback(async (userData: any): Promise<{ success: boolean; error?: string }> => {
    try {
      loginWithSocial(userData.preferredProvider || 'google');
      return { success: true };
    } catch (error: any) {
      return { success: false, error: error.message || 'Registration failed' };
    }
  }, [loginWithSocial]);

  // Header-auth sessions: provide user from localStorage when active.
  const effectiveUser = useMemo(() => {
    if (user) return user;
    if (
      (
        sessionStorage.getItem('testLoginActive') === 'true' ||
        sessionStorage.getItem('botLoginActive') === 'true'
      ) && hasCredentialReadyAuth()
    ) {
      try {
        const stored = getCurrentUser();
        if (stored) return stored;
      } catch (e) { /* ignore */ }
    }
    return null;
  }, [user]);

  const effectiveLoading = useMemo(() => {
    if (
      (
        sessionStorage.getItem('testLoginActive') === 'true' ||
        sessionStorage.getItem('botLoginActive') === 'true'
      ) &&
      hasCredentialReadyAuth() &&
      effectiveUser
    ) return false;
    return loading;
  }, [loading, effectiveUser]);

  const value = useMemo(() => ({
    user: effectiveUser,
    loading: effectiveLoading,
    login,
    logout,
    updateUser,
    register,
    loginWithSocial,
    loginWithEmailOTP,
    isAuthenticated: auth0IsAuthenticated || (!!effectiveUser && hasCredentialReadyAuth()),
    accessToken,
    setIsRequestingToSpot,
    isLoggingOut,
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [
    effectiveUser,
    effectiveLoading,
    login,
    logout,
    updateUser,
    register,
    loginWithSocial,
    loginWithEmailOTP,
    auth0IsAuthenticated,
    accessToken,
    isLoggingOut,
  ]);

  const handleErrorModalClose = () => {
    setErrorModalOpen(false);
    const currentPage = window.location.pathname + window.location.search;
    const pendingSpot = sessionStorage.getItem('pendingGuestSpotRequest');
    const returnAfterLogout = pendingSpot
      ? `/creator/${pendingSpot}/request`
      : currentPage;
    sessionStorage.setItem('returnAfterLogout', returnAfterLogout);
    sessionStorage.removeItem('auth0_rejected_user');
    sessionStorage.removeItem('isSigningIn');

    const isOnRequestPage = Boolean(pendingSpot)
      || currentPage.includes('/request')
      || isRequestingToSpot;

    if (!isOnRequestPage) {
      localStorage.removeItem('anonymousSessionId');
      Object.keys(sessionStorage).forEach(key => {
        if (key.startsWith('requestDraft:') || key.startsWith('videoPitchDraft:')) {
          sessionStorage.removeItem(key);
        }
      });

      (async () => {
        try {
          const { clearAllVideos } = await import('../../utils/indexedDB');
          await clearAllVideos();
        } catch (err) {
          console.error('❌ Failed to clear IndexedDB:', err);
        }
      })();
    }

    auth0Logout({ logoutParams: { returnTo: window.location.origin } });
  };

  return (
    <Auth0UserContext.Provider value={value}>
      {children}
      <LoginErrorModal
        open={errorModalOpen}
        onClose={handleErrorModalClose}
        errorType={errorType}
      />
      <EmailCollectionModal
        open={profile.emailCollectionOpen}
        onEmailProvided={profile.handleEmailProvided}
        onCancel={profile.handleEmailCollectionCancel}
        providerName={
          profile.pendingAuth0User?.sub?.split('|')[0]
            ? profile.pendingAuth0User.sub.split('|')[0].charAt(0).toUpperCase() +
              profile.pendingAuth0User.sub.split('|')[0].slice(1)
            : 'Social login'
        }
      />
      <SocialProfileModal
        open={profile.socialProfileOpen}
        onSubmit={profile.handleSocialProfileProvided}
        onCancel={profile.handleSocialProfileCancel}
      />
      <CompleteProfileModal
        open={profile.completeProfileOpen}
        onSubmit={profile.handleCompleteProfileSubmit}
        onCancel={profile.handleCompleteProfileCancel}
        defaultName={profile.pendingAuth0User?.name || profile.pendingAuth0User?.nickname || ''}
        defaultEmail={profile.collectedEmail || profile.pendingAuth0User?.email || ''}
        defaultImage={profile.pendingAuth0User?.picture || ''}
        accountCreated={profile.profileAccountCreated}
        requiresEmailVerification={Boolean(
          profile.pendingAuth0User
          && profile.pendingAuth0User.email_verified !== true
        )}
      />
    </Auth0UserContext.Provider>
  );
};

export const useAuth0User = (): Auth0UserContextType => {
  const context = useContext(Auth0UserContext);
  if (context === undefined) {
    throw new Error('useAuth0User must be used within an Auth0UserProvider');
  }
  return context;
};
